import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { Users, RefreshCw, Shield, ShieldOff, Edit2, Check, X, UserPlus } from 'lucide-react';

interface AdminUser {
  id: number;
  email: string;
  name: string;
  avatar: string | null;
  roles: string[];
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

const ALL_ROLES = [
  'ROLE_ADMIN',
  'ROLE_GESTOR',
  'ROLE_FINANCIERO',
  'ROLE_IMPRENTA',
  'ROLE_DTF',
  'ROLE_ALMACEN',
  'ROLE_EMBALAJE',
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function RoleBadge({ role }: { role: string }) {
  const label = role.replace('ROLE_', '');
  const isAdmin = role === 'ROLE_ADMIN';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 7px',
      borderRadius: 4,
      fontSize: 10,
      fontFamily: 'JetBrains Mono, monospace',
      fontWeight: 600,
      background: isAdmin ? 'rgba(249,115,22,0.15)' : 'rgba(107,104,128,0.15)',
      color: isAdmin ? '#f97316' : '#8b89a0',
      border: `1px solid ${isAdmin ? 'rgba(249,115,22,0.3)' : 'rgba(107,104,128,0.2)'}`,
      marginRight: 4,
      marginBottom: 2,
    }}>
      {label}
    </span>
  );
}

function UserAvatar({ user }: { user: Pick<AdminUser, 'avatar' | 'name'> }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {user.avatar ? (
        <img
          src={user.avatar}
          alt=""
          style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid #33323d', flexShrink: 0 }}
        />
      ) : (
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: '#27262f', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 13, color: '#f97316', fontWeight: 700, flexShrink: 0,
        }}>
          {user.name[0]?.toUpperCase()}
        </div>
      )}
    </div>
  );
}

function RolesEditor({
  user,
  onSave,
  onCancel,
  saving,
}: {
  user: AdminUser;
  onSave: (roles: string[]) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [selected, setSelected] = useState<string[]>(
    user.roles.filter((r) => r !== 'ROLE_USER')
  );

  const toggle = (role: string) => {
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  return (
    <div style={{
      background: '#1c1b22', border: '1px solid #33323d', borderRadius: 8,
      padding: '12px 14px', minWidth: 260,
    }}>
      <div style={{ fontSize: 11, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', marginBottom: 10 }}>
        ROLES — {user.name}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {ALL_ROLES.map((role) => {
          const active = selected.includes(role);
          return (
            <button
              key={role}
              onClick={() => toggle(role)}
              style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 11,
                fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, cursor: 'pointer',
                background: active ? 'rgba(249,115,22,0.15)' : 'rgba(107,104,128,0.08)',
                color: active ? '#f97316' : '#6b6880',
                border: `1px solid ${active ? 'rgba(249,115,22,0.4)' : 'rgba(107,104,128,0.2)'}`,
                transition: 'all 0.15s',
              }}
            >
              {role.replace('ROLE_', '')}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => onSave(['ROLE_USER', ...selected])}
          disabled={saving}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 5, fontSize: 12, fontWeight: 600,
            background: '#f97316', border: 'none', color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          <Check size={12} /> Guardar
        </button>
        <button
          onClick={onCancel}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 5, fontSize: 12,
            background: 'transparent', border: '1px solid #33323d',
            color: '#6b6880', cursor: 'pointer',
          }}
        >
          <X size={12} /> Cancelar
        </button>
      </div>
    </div>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({ email: '', name: '', password: '', roles: [] as string[] });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => usersApi.createUser(form),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error ?? 'Error al crear el usuario');
    },
  });

  const toggleRole = (role: string) => {
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Crear usuario"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#1c1b22', border: '1px solid #33323d', borderRadius: 10,
        padding: '24px 28px', width: 420, maxWidth: '90vw',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#f4f3f8', fontFamily: 'Syne, sans-serif' }}>
            Crear usuario
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b6880', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {(['email', 'name', 'password'] as const).map((field) => (
            <div key={field} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', marginBottom: 5 }}>
                {field.toUpperCase()}
              </label>
              <input
                type={field === 'password' ? 'password' : field === 'email' ? 'email' : 'text'}
                value={form[field]}
                onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
                placeholder={field === 'email' ? 'usuario@empresa.com' : field === 'name' ? 'Nombre completo' : 'Mínimo 8 caracteres'}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: '#111014', border: '1px solid #33323d', borderRadius: 6,
                  padding: '8px 12px', fontSize: 13, color: '#c4c2d4',
                  outline: 'none',
                }}
              />
            </div>
          ))}

          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', marginBottom: 8 }}>
              ROLES (ROLE_USER se asigna automáticamente)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ALL_ROLES.map((role) => {
                const active = form.roles.includes(role);
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleRole(role)}
                    style={{
                      padding: '4px 10px', borderRadius: 4, fontSize: 11,
                      fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, cursor: 'pointer',
                      background: active ? 'rgba(249,115,22,0.15)' : 'rgba(107,104,128,0.08)',
                      color: active ? '#f97316' : '#6b6880',
                      border: `1px solid ${active ? 'rgba(249,115,22,0.4)' : 'rgba(107,104,128,0.2)'}`,
                    }}
                  >
                    {role.replace('ROLE_', '')}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 6 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px', borderRadius: 6, fontSize: 13,
                background: 'transparent', border: '1px solid #33323d',
                color: '#6b6880', cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                background: '#f97316', border: 'none', color: '#fff',
                cursor: mutation.isPending ? 'not-allowed' : 'pointer',
                opacity: mutation.isPending ? 0.6 : 1,
              }}
            >
              <UserPlus size={14} />
              {mutation.isPending ? 'Creando...' : 'Crear usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const [editingRoles, setEditingRoles] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => usersApi.list(),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      usersApi.toggleActive(id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const updateRolesMutation = useMutation({
    mutationFn: ({ id, roles }: { id: number; roles: string[] }) =>
      usersApi.updateRoles(id, roles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditingRoles(null);
    },
  });

  const users: AdminUser[] = data?.data ?? [];

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1300 }}>
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['admin-users'] })}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(249,115,22,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Users size={18} color="#f97316" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#f4f3f8', fontFamily: 'Syne, sans-serif' }}>
              Gestión de Usuarios
            </h1>
            <p style={{ margin: 0, fontSize: 12, color: '#4a4858' }}>
              {users.length} usuario{users.length !== 1 ? 's' : ''} registrado{users.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6,
              background: '#f97316', border: 'none',
              color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            <UserPlus size={14} />
            Crear usuario
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6,
              background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)',
              color: '#f97316', cursor: 'pointer', fontSize: 13,
              opacity: isFetching ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} style={{ animation: isFetching ? 'spin 1s linear infinite' : 'none' }} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: '#16151c', border: '1px solid #27262f', borderRadius: 10,
        overflow: 'visible',
      }}>
        {isLoading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#4a4858' }}>
            Cargando usuarios...
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#4a4858' }}>
            <Users size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>No hay usuarios</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #27262f' }}>
                  {['Usuario', 'Roles', 'Estado', 'Creado', 'Último acceso', 'Acciones'].map((h) => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left',
                      fontSize: 11, color: '#4a4858',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontWeight: 600, letterSpacing: '0.05em',
                      background: '#111014',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => {
                  const isSelf = u.id === currentUser?.id;
                  const isEditingThisUser = editingRoles === u.id;
                  return (
                    <tr
                      key={u.id}
                      style={{
                        borderBottom: i < users.length - 1 ? '1px solid #1e1d25' : 'none',
                        opacity: u.active ? 1 : 0.55,
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#1a1921')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* Usuario */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <UserAvatar user={u} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#c4c2d4' }}>
                              {u.name}
                              {isSelf && (
                                <span style={{ fontSize: 10, color: '#f97316', marginLeft: 6 }}>(tú)</span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Roles */}
                      <td style={{ padding: '12px 16px', maxWidth: 280 }}>
                        {isEditingThisUser ? (
                          <RolesEditor
                            user={u}
                            onSave={(roles) => updateRolesMutation.mutate({ id: u.id, roles })}
                            onCancel={() => setEditingRoles(null)}
                            saving={updateRolesMutation.isPending}
                          />
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {u.roles
                              .filter((r) => r !== 'ROLE_USER')
                              .map((r) => <RoleBadge key={r} role={r} />)}
                            {u.roles.filter((r) => r !== 'ROLE_USER').length === 0 && (
                              <span style={{ fontSize: 12, color: '#33323d' }}>Sin roles extra</span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Estado */}
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                          background: u.active ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                          color: u.active ? '#22c55e' : '#ef4444',
                          border: `1px solid ${u.active ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                        }}>
                          <span style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: u.active ? '#22c55e' : '#ef4444',
                          }} />
                          {u.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Creado */}
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 12, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace' }}>
                          {formatDate(u.createdAt)}
                        </span>
                      </td>

                      {/* Último acceso */}
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 12, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace' }}>
                          {formatDate(u.lastLoginAt)}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {/* Edit roles */}
                          {!isEditingThisUser && (
                            <button
                              onClick={() => setEditingRoles(u.id)}
                              title="Editar roles"
                              style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '5px 10px', borderRadius: 5, fontSize: 12,
                                background: 'rgba(107,104,128,0.1)', border: '1px solid #33323d',
                                color: '#8b89a0', cursor: 'pointer',
                              }}
                            >
                              <Edit2 size={12} /> Roles
                            </button>
                          )}

                          {/* Toggle active */}
                          {!isSelf && (
                            <button
                              onClick={() => toggleActiveMutation.mutate({ id: u.id, active: !u.active })}
                              disabled={toggleActiveMutation.isPending}
                              title={u.active ? 'Desactivar usuario' : 'Activar usuario'}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '5px 10px', borderRadius: 5, fontSize: 12,
                                background: u.active
                                  ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                                border: `1px solid ${u.active ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
                                color: u.active ? '#ef4444' : '#22c55e',
                                cursor: toggleActiveMutation.isPending ? 'not-allowed' : 'pointer',
                                opacity: toggleActiveMutation.isPending ? 0.6 : 1,
                              }}
                            >
                              {u.active ? <ShieldOff size={12} /> : <Shield size={12} />}
                              {u.active ? 'Desactivar' : 'Activar'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
